import { ChangeEvent } from 'react';
import { Input, InputProps } from 'antd';
import { Controller, FieldPath, FieldValues } from 'react-hook-form';
import { useAdditionalFormContext } from '../AdditionalFormContext';
import { useGetIdFromName } from '../utils';
import { TextAreaProps } from 'antd/es/input';

export type FormInputProps<
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
> = {
  id?: string;
  name: Name;
  deps?: string | string[];
} & (
  | (Omit<InputProps, 'id' | 'rows'> & { rows?: never })
  | (Omit<TextAreaProps, 'id'> & { rows: number })
);

type FormInputImplementationProps = {
  id?: string;
  name: string;
  rows?: number;
  deps?: string | string[];
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  [key: string]: unknown;
};

export const FormInput = <
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
>(
  props: FormInputProps<FormData, Name>,
) => {
  const getIdFromName = useGetIdFromName();
  const {
    name,
    id = getIdFromName(name),
    onChange,
    deps,
    ...rest
  } = props as FormInputImplementationProps;
  const { onChange: formOnChange } = useAdditionalFormContext();

  const Component: any = 'rows' in rest ? Input.TextArea : Input;

  return (
    <Controller
      name={name}
      render={({ field }) => {
        const { value, disabled } = field;
        const handleChange = (
          event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
          field.onChange(event);
          onChange?.(event);
          formOnChange?.();
        };
        return (
          <Component
            disabled={disabled}
            {...rest}
            id={id}
            value={value}
            onChange={handleChange}
          />
        );
      }}
      rules={{ deps }}
    />
  );
};
